import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabase";
import { trackSignUp, trackLogin } from "../lib/analytics";
import type {
  User,
  Session,
  AuthError as SupabaseAuthError,
} from "@supabase/supabase-js";
import type { Database } from "../lib/database.types";

type Profile = Database["public"]["Tables"]["profiles"]["Row"];
type ProfileInsert = Database["public"]["Tables"]["profiles"]["Insert"];
type AuthError = SupabaseAuthError | { message: string; code?: string };

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
}

interface AuthResponse {
  data: { user: User | null; session: Session | null } | null;
  error: AuthError | null;
}

interface AuthContextType extends AuthState {
  signUp: (
    email: string,
    password: string,
    name: string,
    instagramHandle?: string,
    supportedCountry?: string
  ) => Promise<AuthResponse>;
  signIn: (email: string, password: string) => Promise<AuthResponse>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfile: (updates: {
    instagram_handle?: string | null;
    supported_country?: string | null;
  }) => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    profile: null,
    loading: true,
  });

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        // If profile doesn't exist (PGRST116), that's okay - we'll create it
        if (error.code === "PGRST116") {
          return null;
        }
        console.error("Failed to fetch profile:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Error fetching profile:", error);
      return null;
    }
  }, []);

  const createProfile = useCallback(
    async (
      user: User,
      name: string,
      instagramHandle?: string,
      supportedCountry?: string
    ) => {
      try {
        const profileData: ProfileInsert = {
          id: user.id,
          email: user.email!,
          name,
          instagram_handle: instagramHandle || null,
          supported_country: supportedCountry || null,
          total_points: 0,
          total_predictions: 0,
          correct_predictions: 0,
        };
        const { data, error } = await supabase
          .from("profiles")
          .insert(profileData)
          .select()
          .single();

        if (error) {
          console.error("Failed to create profile:", error);
          return null;
        }

        return data;
      } catch (error) {
        console.error("Error creating profile:", error);
        return null;
      }
    },
    []
  );

  useEffect(() => {
    // Listen for auth changes - this handles session restoration automatically
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setAuthState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }));

      if (event === "INITIAL_SESSION" && session?.user) {
        fetchProfile(session.user.id).then((profile) => {
          setAuthState((prev) => ({
            ...prev,
            profile,
          }));
        });
      }

      if (event === "SIGNED_OUT") {
        setAuthState((prev) => ({
          ...prev,
          user: null,
          session: null,
          profile: null,
        }));
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, createProfile]);

  // Subscribe to real-time profile updates
  useEffect(() => {
    if (!authState.user) return;

    const channel = supabase
      .channel(`profile-${authState.user.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "profiles",
          filter: `id=eq.${authState.user.id}`,
        },
        (payload) => {
          // Update profile when it changes (e.g., points awarded, predictions incremented)
          setAuthState((prev) => ({
            ...prev,
            profile: payload.new as Profile,
          }));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [authState.user]);

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      instagramHandle?: string,
      supportedCountry?: string
    ) => {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
              instagram_handle: instagramHandle,
              supported_country: supportedCountry,
            },
            // Note: To disable email confirmation, go to Supabase Dashboard:
            // Authentication > Providers > Email > Toggle OFF "Confirm email"
          },
        });

        if (error) throw error;

        // Create profile when user is created, regardless of email confirmation
        if (data.user) {
          // Create profile immediately when user is created
          // This ensures profile exists even if email confirmation is required
          await createProfile(
            data.user,
            name,
            instagramHandle,
            supportedCountry
          );

          // If session exists, user is immediately signed in (email confirmation disabled)
          if (data.session) {
            const profile = await fetchProfile(data.user.id);
            setAuthState((prev) => ({
              ...prev,
              user: data.user,
              session: data.session,
              profile,
              loading: false,
            }));
            // Track sign up
            trackSignUp("email");
            return { data, error: null };
          } else {
            // User created but email confirmation required
            // Profile is already created, will be fetched when they sign in
            setAuthState((prev) => ({
              ...prev,
              loading: false,
            }));
            return {
              data,
              error: {
                message:
                  "Please check your email to confirm your account. Then sign in.",
              },
            };
          }
        }

        // No user created
        setAuthState((prev) => ({
          ...prev,
          loading: false,
        }));
        return { data, error: null };
      } catch (error) {
        setAuthState((prev) => ({
          ...prev,
          loading: false,
        }));
        const authError: AuthError =
          error instanceof Error
            ? { message: error.message }
            : { message: "Failed to sign up" };
        return { data: null, error: authError };
      }
    },
    [createProfile, fetchProfile]
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        // Fetch profile first, then update state atomically
        let profile = null;
        if (data.user) {
          try {
            profile = await fetchProfile(data.user.id);
          } catch (profileError) {
            console.error(
              "Failed to fetch profile during sign in:",
              profileError
            );
            // Don't fail the sign in if profile fetch fails
            // The onAuthStateChange listener will handle it
          }
        }

        // Update state atomically with all auth data
        setAuthState((prev) => ({
          ...prev,
          user: data.user,
          session: data.session,
          profile,
        }));

        // Track login
        trackLogin("email");

        return { data, error: null };
      } catch (error) {
        const authError: AuthError =
          error instanceof Error
            ? { message: error.message }
            : { message: "Failed to sign in" };
        return { data: null, error: authError };
      }
    },
    [fetchProfile]
  );

  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
    } catch (error) {
      console.error("Failed to sign out:", error);
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    if (authState.user) {
      const profile = await fetchProfile(authState.user.id);
      setAuthState((prev) => ({
        ...prev,
        profile,
      }));
    }
  }, [authState.user, fetchProfile]);

  const updateProfile = useCallback(
    async (updates: {
      instagram_handle?: string | null;
      supported_country?: string | null;
    }) => {
      if (!authState.user) {
        throw new Error("User must be logged in to update profile");
      }

      try {
        const { error } = await supabase
          .from("profiles")
          .update(updates)
          .eq("id", authState.user.id);

        if (error) throw error;

        // Refresh profile to get updated data
        await refreshProfile();
      } catch (error) {
        console.error("Failed to update profile:", error);
        throw error;
      }
    },
    [authState.user, refreshProfile]
  );

  // Check if current user is admin
  const isAdmin = authState.user?.email === "elkhayatreda@gmail.com";

  const value: AuthContextType = {
    ...authState,
    signUp,
    signIn,
    signOut,
    refreshProfile,
    updateProfile,
    isAdmin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
